/**
 * useInboxChat
 *
 * Owns all chat-node-list state/logic for the Inbox page (the thin `useInbox`
 * orchestrator composes this sub-hook in Task 10). Ports the chat half of
 * Flutter's MessagesScreen `State`:
 *   - paginated fetch (lib/presentation/screens/messages/messages_screen.dart:237-321,
 *     `_getChatItems`) — peer decryption already done in `FetchChatNodesUseCase`
 *     (Task 5); `hasMore = pageLen < MESSAGES_LIST_PAGINATION ? false : true`
 *     (messages_screen.dart:267-269).
 *   - the live subscription fan-out (messages_screen.dart:521-565,
 *     `_subscribeToChatUpdates`): `added` (replace by `chatId` if present, else
 *     append) / `modified` (replace by `chatId`) / `removed` (drop by `chatId`),
 *     each followed by a `timestamp` desc re-sort.
 *   - the unread-message predicate (messages_screen.dart:586-612,
 *     `_checkChatUpdatesBadge`): any chat node where `idTo === myId && !seenByPeer`.
 *
 * `isFirstLoad` is CHAT-OWNED (messages_screen.dart:318/460): it is cleared ONLY
 * by `getChatNodes` — both the empty-first-page branch (which still starts the
 * subscription, messages_screen.dart:271-283, via `_subscribeToChatUpdates`
 * line 460) and the populated branch (messages_screen.dart:312-319). The
 * notifications path NEVER touches `_isFirstLoad`. Because Flutter fires
 * `_getChatItems()` without `await` in `_initData` (messages_screen.dart:95-104),
 * the first-load spinner clears off the CHAT fetch — so Task 10 surfaces this
 * `isFirstLoad` and Task 15 reads it for the spinner gate.
 *
 * Keying note: `ChatMessageNodeEntity` has no `documentId`; the node doc lives
 * at `/message_nodes/{groupChatId}` so its doc id == `chatId`. Flutter keys the
 * fan-out by `documentId` (messages_screen.dart:525/541/552); web keys by
 * `chatId` (Task 3 records this). The decrypted text is read from the camelCase
 * `node.lastContent` (the entity field) — never the DTO's `last_content`.
 *
 * No UI, no `useBasePage`, no `eventBus.emit`, no `items` composition — Task 10's
 * `useInbox` composer owns the lifecycle bridge, the combined badge emit, and the
 * `items` view-model. Web drops Flutter's `saveHiveChatNotification` /
 * local-notification side effects per the task prompt (point 6).
 *
 * `currentUserId` is passed in by the composer (the sub-hook never reads Redux
 * directly) so the auth gate stays in the composer and this hook is testable in
 * isolation.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Unsubscribe } from 'firebase/firestore';
import type { ChatMessageNodeEntity } from '@/domain/entities/chat/chatMessageNodeEntity';
import { FetchChatNodesUseCase } from '@/domain/usecases/chat/fetchChatNodesUseCase';
import {
  SubscribeToChatNodesUseCase,
  type ChatNodeChange,
} from '@/domain/usecases/chat/subscribeToChatNodesUseCase';
import { RemoteDocumentChangeType } from '@/data/types/remoteDocumentChangeType';
import { SERVER_FETCH_CONSTANTS } from '@/domain/constants/serverFetchConstants';
import { appLogger } from '@/domain/services/analytics/appLogger';

const { MESSAGES_LIST_PAGINATION } = SERVER_FETCH_CONSTANTS;

/** Newest-first sort (messages_screen.dart:530/537/546 — `b.timestamp - a.timestamp`). */
const sortByTimestampDesc = (
  a: ChatMessageNodeEntity,
  b: ChatMessageNodeEntity,
): number => b.timestamp - a.timestamp;

export interface UseInboxChatParams {
  /** Resolved by the Task-10 composer (currentUser.firebaseId); owns the auth gate. */
  currentUserId: string | null | undefined;
}

export interface UseInboxChatResult {
  chatNodes: ChatMessageNodeEntity[];
  hasMoreChatNodes: boolean;
  isFetchingChatNodes: boolean;
  /**
   * Chat-owned first-load flag (messages_screen.dart:318/460). Cleared ONLY by
   * `getChatNodes`. Task 10 surfaces it; Task 15 reads it for the spinner gate.
   */
  isFirstLoad: boolean;
  /** Any chat node where `idTo === currentUserId && !seenByPeer`. */
  hasUnreadMessage: boolean;
  getChatNodes: () => Promise<void>;
  /** `onReachedEnd` target (Task 15). Same body as `getChatNodes`. */
  loadMoreChatNodes: () => Promise<void>;
}

export function useInboxChat(params: UseInboxChatParams): UseInboxChatResult {
  const { currentUserId } = params;

  const [chatNodes, setChatNodes] = useState<ChatMessageNodeEntity[]>([]);
  const [hasMoreChatNodes, setHasMoreChatNodes] = useState(true);
  const [isFetchingChatNodes, setIsFetchingChatNodes] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  // Refs mirror live state for the guards inside the stable `getChatNodes`
  // callback, which would otherwise close over stale values (useWallet pattern).
  const isFetchingChatRef = useRef(false);
  const hasMoreChatRef = useRef(true);
  const isFirstLoadRef = useRef(true);
  // Cursor for the NEXT page = timestamp of the last (oldest) node in the page
  // just fetched (messages_screen.dart:285-286 advances `_lastFetchedChatDocument`).
  const lastChatTimestampRef = useRef<number | null>(null);
  // The first page's newest node's timestamp, captured to start the subscription
  // with the `>=` cursor (messages_screen.dart:313 `_mostRecentChatDocument`).
  // Stays `undefined` on an empty first page so the subscription omits the
  // `timestamp` filter (messages_screen.dart:474-475).
  const mostRecentChatTimestampRef = useRef<number | undefined>(undefined);
  const chatUnsubscribeRef = useRef<Unsubscribe | null>(null);
  const mountedRef = useRef(true);
  const currentUserIdRef = useRef(currentUserId);

  useEffect(() => {
    isFetchingChatRef.current = isFetchingChatNodes;
    hasMoreChatRef.current = hasMoreChatNodes;
    isFirstLoadRef.current = isFirstLoad;
    currentUserIdRef.current = currentUserId;
  }, [isFetchingChatNodes, hasMoreChatNodes, isFirstLoad, currentUserId]);

  /**
   * Apply one batch of live changes to the chat-node list (fan-out).
   * Flutter: messages_screen.dart:521-565. Keyed by `chatId` (== the node doc
   * id; the entity has no `documentId`). Each branch re-sorts `timestamp` desc.
   */
  const applyChatChanges = useCallback((changes: ChatNodeChange[]) => {
    if (!mountedRef.current) return;
    setChatNodes(prev => {
      let next = prev;
      let mutated = false;
      for (const change of changes) {
        const node = change.data;
        if (change.type === RemoteDocumentChangeType.added) {
          // `added`: replace an existing node with the same `chatId` (a
          // last-message update echoed as an add), else append.
          const idx = next.findIndex(n => n.chatId === node.chatId);
          if (idx !== -1) {
            if (!mutated) {
              next = next.slice();
              mutated = true;
            }
            next[idx] = node;
          } else {
            if (!mutated) {
              next = next.slice();
              mutated = true;
            }
            next.push(node);
          }
        } else if (change.type === RemoteDocumentChangeType.modified) {
          // `modified`: replace by `chatId`; no-op if absent.
          const idx = next.findIndex(n => n.chatId === node.chatId);
          if (idx !== -1) {
            if (!mutated) {
              next = next.slice();
              mutated = true;
            }
            next[idx] = node;
          }
        } else if (change.type === RemoteDocumentChangeType.removed) {
          // `removed`: drop by `chatId`; no-op if absent.
          const idx = next.findIndex(n => n.chatId === node.chatId);
          if (idx !== -1) {
            if (!mutated) {
              next = next.slice();
              mutated = true;
            }
            next.splice(idx, 1);
          }
        }
      }
      if (!mutated) return prev;
      next.sort(sortByTimestampDesc);
      return next;
    });
  }, []);

  /**
   * Start the live subscription ONCE (first load only). Cursor:
   *   - non-empty first page → `mostRecentChatTimestampRef.current` (`>=` filter,
   *     messages_screen.dart:466-473).
   *   - empty first page → `undefined` (unfiltered subscription so brand-new
   *     conversations are still caught, messages_screen.dart:474-475). NO
   *     `0`/`Date.now()` fallback — that would diverge from Flutter.
   */
  const startChatSubscription = useCallback(() => {
    if (chatUnsubscribeRef.current) return;
    const owner = currentUserIdRef.current;
    if (!owner) return;
    const useCase = new SubscribeToChatNodesUseCase();
    chatUnsubscribeRef.current = useCase.execute(
      owner,
      mostRecentChatTimestampRef.current,
      applyChatChanges,
    );
  }, [applyChatChanges]);

  // Flutter: messages_screen.dart:237-321 (`_getChatItems`).
  const getChatNodes = useCallback(async () => {
    // Guard (messages_screen.dart:242-247).
    if (isFetchingChatRef.current || !hasMoreChatRef.current) {
      return;
    }

    const owner = currentUserIdRef.current;
    if (!owner) return;

    isFetchingChatRef.current = true;
    setIsFetchingChatNodes(true);

    try {
      const useCase = new FetchChatNodesUseCase();
      const result = await useCase.execute(
        owner,
        lastChatTimestampRef.current,
        MESSAGES_LIST_PAGINATION,
      );

      if (!mountedRef.current) return;

      const page = result.nodes;
      const pageLen = page.length;

      // `hasMore = pageLen < limit ? false : true` (messages_screen.dart:267-269).
      const nextHasMore = pageLen === MESSAGES_LIST_PAGINATION;
      hasMoreChatRef.current = nextHasMore;
      setHasMoreChatNodes(nextHasMore);

      // Empty-first-page branch (messages_screen.dart:271-283): start the
      // subscription with NO cursor and clear `isFirstLoad`, then return.
      // `_mostRecentChatDocument` stays null → the subscription omits the
      // `timestamp` filter (messages_screen.dart:474-475).
      if (pageLen === 0) {
        if (isFirstLoadRef.current) {
          // Subscription cursor stays `undefined` (no `mostRecentChatTimestamp`).
          startChatSubscription();
          isFirstLoadRef.current = false;
          setIsFirstLoad(false);
        }
        return;
      }

      // Non-empty: advance the next-page cursor to the oldest node's timestamp
      // (messages_screen.dart:285-286). The service returns `lastTimestamp`.
      lastChatTimestampRef.current = result.lastTimestamp;

      // Append (messages_screen.dart:309). The page is already desc by timestamp.
      setChatNodes(prev => [...prev, ...page]);

      // First-load (non-empty) branch (messages_screen.dart:312-319): capture
      // the newest node's timestamp, start the subscription with that `>=`
      // cursor, then clear `isFirstLoad`.
      if (isFirstLoadRef.current) {
        mostRecentChatTimestampRef.current = page[0]?.timestamp;
        startChatSubscription();
        isFirstLoadRef.current = false;
        setIsFirstLoad(false);
      }
    } catch (error) {
      appLogger.error('Error fetching chat nodes', 'useInboxChat.getChatNodes', {
        error: error as Error,
      });
    } finally {
      isFetchingChatRef.current = false;
      if (mountedRef.current) setIsFetchingChatNodes(false);
    }
  }, [startChatSubscription]);

  // Flutter `_scrollListener` paginates ONLY the chat list
  // (messages_screen.dart:130-140) — same body as `getChatNodes`. Wired to the
  // page's `onReachedEnd` (Task 15). The notifications list does NOT paginate
  // on scroll; that asymmetry is preserved (notifications grow only via the
  // "More updates" tap, Task 8).
  const loadMoreChatNodes = useCallback(() => getChatNodes(), [getChatNodes]);

  // Flutter: messages_screen.dart:586-612 (`_checkChatUpdatesBadge`) — any chat
  // node addressed to me (`idTo === myId`) that the peer (sender) has not had
  // marked seen (`!seenByPeer`). The combined `eventBus` emit lives in Task 10;
  // this sub-hook only exposes the predicate result.
  const hasUnreadMessage = useMemo(() => {
    const owner = currentUserId;
    if (!owner) return false;
    return chatNodes.some(node => node.idTo === owner && node.seenByPeer === false);
  }, [chatNodes, currentUserId]);

  // Single teardown point for the live subscription. Now that one shared
  // `useInbox` core (mounted by `InboxProvider` for the app session) owns this
  // sub-hook, this cleanup fires ONLY when that core unmounts — effectively app
  // shutdown / sign-out — NOT when the user leaves the `/inbox` route. Keeping
  // the subscription alive across `/inbox` exits is what keeps the TopBar unseen
  // badge live for incoming chats while the Inbox page is closed (finding 1).
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (chatUnsubscribeRef.current) {
        chatUnsubscribeRef.current();
        chatUnsubscribeRef.current = null;
      }
    };
  }, []);

  return {
    chatNodes,
    hasMoreChatNodes,
    isFetchingChatNodes,
    isFirstLoad,
    hasUnreadMessage,
    getChatNodes,
    loadMoreChatNodes,
  };
}
